import { initializeOwaConnectedAccount } from '../utils/initializeOwaConnectedAccount';
import { initializeOwaAccountsStore } from '../actions/publicActions';
import {
    ConnectedAccountResponse,
    lazyGetAllConnectedAccounts,
    GetAllConnectedAccountsResponse,
    OAuthProviderType,
} from 'owa-link-account';
import {
    AccountProviderType,
    OWAConnectedAccountState,
    getOWAConnectedAccountStatus,
    connectedAccountsListRequested,
    getAccountsStoreState,
    AccountsStoreState,
    setAccountsStoreState,
    isMonarchMultipleAccountsEnabled,
} from 'owa-accounts-store';
import { orchestrator } from 'satcheljs';
import { assertNever } from 'owa-assert';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import { addSeconds, now } from 'owa-datetime';
import { logUsage } from 'owa-analytics';
import { tokenExpiryBucket } from '../utils/tokenExpiryBuckets';
import { allCalendarAccountsAndCacheLoaded } from 'owa-calendar-account-store';
import { OwaConnectedAccountInitSource } from '../schema/OwaConnectedAccountInitSource';
import { initializeAccountsStore } from 'owa-mail-account-store-init';
import { initializeOwaAccountsStoreFromAccountSourceList } from './initializeOwaAccountStoreFromAccountSourceList';

/**
 * This orchestrator is triggered from the modules boot code.
 * It looks up the existing connected accounts in the server,
 * and initializes these accounts
 */
orchestrator(initializeOwaAccountsStore, async () => {
    if (isMonarchMultipleAccountsEnabled()) {
        // Use the new Account store to initialize the OWA connected account store.
        await initializeAccountsStore();
        await initializeOwaAccountsStoreFromAccountSourceList();
        return;
    }
    const initialStoreState = getAccountsStoreState();

    if (
        initialStoreState == AccountsStoreState.AccountsLoadSuccessful ||
        initialStoreState == AccountsStoreState.Loading
    ) {
        return;
    }
    setAccountsStoreState(AccountsStoreState.Loading);
    const response = await lazyGetAllConnectedAccounts.importAndExecute();
    const successResponse = response as GetAllConnectedAccountsResponse;
    let isSuccess = response?.isSuccess;

    if (
        successResponse.ConnectedAccountResponses &&
        typeof successResponse.ConnectedAccountResponses.filter == 'function'
    ) {
        if (successResponse.ConnectedAccountResponses.length == 0) {
            allCalendarAccountsAndCacheLoaded(isSuccess /* isSuccess */);
        }
        await Promise.all(
            successResponse.ConnectedAccountResponses.filter(isSupportedConnectedAccount).map(
                async account => {
                    const accountProviderType = getAccountProviderType(account.providerType);
                    // track number of times owa is loaded with existing connected account
                    const tokenExpiration = parseInt(account.token.expiresIn);
                    logUsage(
                        'hasConnectedAccount',
                        {
                            tokenExpiry: tokenExpiration,
                            type_1: accountProviderType,
                            tokenExpiryLowerBound_3: tokenExpiryBucket(tokenExpiration)
                                .lowerBoundSeconds,
                            tokenExpirtyUpperBound_4: tokenExpiryBucket(tokenExpiration)
                                .upperBoundSeconds,
                        },
                        { isCore: true }
                    );
                    await initializeOwaConnectedAccount(
                        {
                            userIdentity: account.userAccount.emailAddress,
                            accountId: account.userAccount.id,
                            accountUniqueId: account.userAccount.accountUniqueId,
                            anchorMailbox: account.userAccount.routingHint,
                            token: account.token.accessToken,
                            tokenExpiry: addSeconds(now(), tokenExpiration),
                            accountProviderType: accountProviderType,
                            accountState: OWAConnectedAccountState.Valid, // always set the accountState as valid here, because the service call does not refresh token at this point. Refresh token api will take care of setting right state
                        },
                        OwaConnectedAccountInitSource.Init,
                        false
                    );
                    if (
                        getOWAConnectedAccountStatus(account.userAccount.emailAddress) !=
                        OWAConnectedAccountState.Valid
                    ) {
                        isSuccess = false;
                    }
                }
            )
        );
    }
    connectedAccountsListRequested(isSuccess);
});

function isSupportedConnectedAccount(connectedAccount: ConnectedAccountResponse): boolean {
    switch (connectedAccount.providerType) {
        case OAuthProviderType.Msa:
        case OAuthProviderType.MsaInt:
            return true;
        case OAuthProviderType.Google:
            return isFeatureEnabled('cal-addCalendar-google');
        case OAuthProviderType.TeamSnap:
            return false;
        case OAuthProviderType.ICloud:
            return isFeatureEnabled('cal-addCalendar-iCloud');
        case OAuthProviderType.Yahoo:
            return isFeatureEnabled('cal-addCalendar-Yahoo');
        default:
            return assertNever(connectedAccount.providerType as never);
    }
}

function getAccountProviderType(providerType: OAuthProviderType): AccountProviderType {
    switch (providerType) {
        case OAuthProviderType.Msa:
            return 'Outlook';
        case OAuthProviderType.Google:
            return 'Google';
        case OAuthProviderType.ICloud:
            return 'ICloud';
        case OAuthProviderType.Yahoo:
            return 'Yahoo';
        default:
            return assertNever(providerType as never);
    }
}
